import { useId } from 'react';
import Button from './core/Button';
import { FormattedMessage } from 'react-intl';
import { Bearer } from './Bearer';
import { FavoredBadge } from './FavoredBadge';
import { getTravelCardSourceBadge } from '@/helpers/bearers';
import { cn } from '@/helpers/classnames';
import { type BearerListItem } from '@/hooks/useBearerMapping';

interface BearerSelectionProps {
  bearers: BearerListItem[];
  selectedBearer?: string;
  onBearerSelect: (bearerId: string) => void;
  mostRecentBobcatBearerId?: string;
  className?: string;
}

export const BearerSelection = ({
  bearers,
  selectedBearer,
  onBearerSelect,
  mostRecentBobcatBearerId,
  className,
}: BearerSelectionProps) => {
  const labelId = useId();

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <p id={labelId}>
        <FormattedMessage defaultMessage="Välj bärare för biljetten" />
      </p>
      <ul aria-labelledby={labelId} className="flex flex-col gap-1">
        {bearers.map((bearer) => {
          const isSelected = selectedBearer === bearer.id;
          const isFavored = bearer.id === mostRecentBobcatBearerId;
          const sourceBadge = getTravelCardSourceBadge(bearer);
          return (
            <li key={`${bearer.id}-${bearer.source}`}>
              <Button
                className="min-h-16 w-full flex-wrap justify-start truncate px-2 font-normal"
                variant={isSelected ? 'filled' : 'outline'}
                aria-pressed={isSelected}
                onClick={() => onBearerSelect(bearer.id)}
              >
                <Bearer bearer={bearer} />
                {(sourceBadge || isFavored) && (
                  <div className="ml-auto flex items-center gap-2">
                    {sourceBadge && (
                      <span className="ml-auto whitespace-nowrap rounded bg-background-100 px-1.5 py-0.5 text-xs font-medium text-text-700">
                        {sourceBadge === 'alltix' ? (
                          <FormattedMessage defaultMessage="Systemägt resekort" />
                        ) : (
                          <FormattedMessage defaultMessage="Från E-post i appen / Mina sidor" />
                        )}
                      </span>
                    )}
                    {isFavored && <FavoredBadge className="text-sm" />}
                  </div>
                )}
              </Button>
            </li>
          );
        })}
        {bearers.length === 0 && (
          <li className="italic text-text-700">
            <FormattedMessage defaultMessage="Inga bärare finns tillgängliga för resenären." />
          </li>
        )}
      </ul>
    </div>
  );
};
